import React, {useEffect, useState, useRef} from 'react';
import {injectIntl} from 'react-intl';
import uniqueId from 'lodash/uniqueId';

//styled-components
import {
  SelectWrapper,
  Label,
  Select,
  OptionsWrapper,
  Option,
  UserInput,
  Chevron,
  CheckMark,
  Close,
  SkeletonDiv,
  SkeletonOption,
  FeedbackText
} from './styles';
import Dropdown from 'components/Dropdown';
import Icon from 'components/Icon';

//messages
import messages from './messages';

const SelectComponent = props => {
  const {
    intl: {formatMessage},
    disabled,
    feedbackText,
    label,
    style,
    display,
    width,
    allowClear,
    placeholder,
    showSearch,
    options,
    notFoundContent,
    selectedValue,
    defaultValue,
    field,
    form,
    inputId = uniqueId('select-'),
    loading
  } = props;

  const [selectorOpened, setSelectorOpened] = useState(false);
  const [userInput, setUserInput] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    checkIfAtBottom({target: wrapperRef.current});
  }, [selectorOpened, userInput, options]);

  const toggleSelector = e => {
    if (e?.preventDefault) e.preventDefault();
    if (disabled) return;
    setSelectorOpened(!selectorOpened);
  };

  const onChange = value => {
    const {onChange} = props;

    if (disabled) return;
    if (form && field) form.$(field).sync(value);
    if (onChange) onChange(value);

    setUserInput('');
    toggleSelector();
  };

  const updateUserInput = e => {
    const {onSearch} = props;
    setUserInput(e.target.value);
    if (onSearch) onSearch(userInput);
    if (!selectorOpened) toggleSelector();
  };

  const handleInputKeydown = event => {
    // enter key pressed
    if (event.keyCode === 13 && options.length) {
      const firstAvailableOption = options[0].id;
      onChange(firstAvailableOption);
    }

    // esc key pressed
    if (event.keyCode === 27) {
      toggleSelector();
    }
  };

  const isSelected = option => selectedOptionId === option.id;

  const getSelectedOptionId = () => {
    if (form && field) return form.values()[field];

    if (selectedValue) return selectedValue;

    if (defaultValue) return defaultValue;

    return null;
  };

  const checkIfAtBottom = e => {
    const {onGetToBottom} = props;
    if (options.length && !filteredOptions.length) {
      onGetToBottom();
    }
    if (!selectorOpened || !onGetToBottom) return;
    const bottom = e.target?.scrollHeight - e.target?.scrollTop === e.target?.clientHeight;
    if (bottom) {
      onGetToBottom();
    }
  };

  const getFilteredOptions = () => {
    const {onSearch} = props; // if custom onSearch function is provided, use it to filter options - return default options
    if (userInput && !onSearch) {
      return options.filter(option => option.name.toLowerCase().includes(userInput.toLowerCase()));
    }

    return options;
  };

  const selectedOptionId = getSelectedOptionId();
  const selectedOption = options?.find(option => option.id === selectedOptionId) || null;

  const filteredOptions = getFilteredOptions();

  const renderOptions = () => {
    if (!loading && !filteredOptions.length) {
      return (
        <OptionsWrapper style={style?.OptionsWrapper}>
          <Option disabled>{notFoundContent || formatMessage(messages.nothingToSelect)}</Option>
        </OptionsWrapper>
      );
    }

    return (
      <OptionsWrapper role="combobox" style={style?.OptionsWrapper} onScroll={checkIfAtBottom} ref={wrapperRef}>
        {filteredOptions?.map(option => (
          <Option
            key={option.id}
            onClick={() => !option.disabled && onChange(option.id)}
            selected={isSelected(option)}
            role="option"
            aria-selected={isSelected(option)}
            disabled={option.disabled}
            style={option.style}
            hidden={option.hidden}
          >
            {option.iconId && <Icon iconId={option.iconId} />}
            {option.name}
            {isSelected(option) && <CheckMark />}
          </Option>
        ))}
        {loading && (
          <>
            <SkeletonOption>
              <SkeletonDiv width="200px" />
            </SkeletonOption>
            <SkeletonOption>
              <SkeletonDiv width="200px" />
            </SkeletonOption>
            <SkeletonOption>
              <SkeletonDiv width="200px" />
            </SkeletonOption>
          </>
        )}
      </OptionsWrapper>
    );
  };

  return (
    <SelectWrapper id={`select-wrapper-${inputId}`} style={style} display={display} role="listbox" disabled={disabled}>
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Dropdown
        open={selectorOpened}
        visible={selectorOpened}
        toggle={toggleSelector}
        onVisibleChange={toggleSelector}
        overlayClassName="c-header__popover__inner"
        placement="bottomRight"
        trigger="click"
        content={renderOptions()}
        getPopupContainer={() => document.getElementById(`select-wrapper-${inputId}`)}
        width={width}
      >
        <Select
          disabled={disabled}
          iconId="sort"
          iconOnRight
          label={selectedOption?.name ?? placeholder}
          open={!disabled && selectorOpened}
          showSearch={showSearch}
          style={style?.Select}
          width={width}
        >
          {
            <UserInput
              autoFocus={selectorOpened && showSearch}
              id={inputId}
              style={style?.Input}
              placeholder={selectedOption?.name ?? placeholder}
              readOnly={!showSearch}
              value={selectorOpened ? userInput : selectedOption?.name ?? ''}
              disabled={disabled}
              onKeyDown={handleInputKeydown}
              autoComplete="off"
              {...(showSearch && {onChange: updateUserInput})}
            />
          }
          {allowClear && selectedOption && !selectorOpened ? (
            <Close id="close-button" onClick={() => onChange(null)} />
          ) : (
            <Chevron opened={selectorOpened} />
          )}
        </Select>
      </Dropdown>
      {feedbackText && <FeedbackText>{feedbackText}</FeedbackText>}
    </SelectWrapper>
  );
};

export default injectIntl(SelectComponent);
